<template>
  <permission-gate
    v-slot="{ canEdit }"
    moduleKey="materiales"
  >
    <main-breadcrumb
      :items="items"
      showBackButton
    ></main-breadcrumb>
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Listado de Items</custom-title>
      </template>
      <template #right>
        <template v-if="!editingCosts">
          <el-tooltip
            :disabled="canEdit"
            content="El usuario no posee permisos de edición"
            placement="top"
          >
            <el-button
              type="primary"
              size="default"
              :icon="IconAdd"
              :class="{ 'button-not-allowed': !canEdit }"
              :disabled="!canEdit"
              @click="newMaterial"
            >
              Nuevo Item
            </el-button>
          </el-tooltip>
          <el-tooltip
            :disabled="canEdit"
            content="El usuario no posee permisos de edición"
            placement="top"
          >
            <el-button
              type="warning"
              size="default"
              :icon="IconCashOpen"
              :class="{ 'button-not-allowed': !canEdit }"
              :disabled="!canEdit"
              @click="enterCostMode"
            >
              Actualizar Costos
            </el-button>
          </el-tooltip>
        </template>
        <template v-else>
          <el-tooltip
            v-if="!canEdit"
            content="El usuario no posee permisos de edición"
            placement="top"
          >
            <el-button
              type="success"
              size="default"
              :icon="IconContentSave"
              disabled
            >
              Guardar Costos
            </el-button>
          </el-tooltip>
          <el-button
            v-else
            type="success"
            size="default"
            :icon="IconContentSave"
            @click="saveCosts"
          >
            Guardar Costos
          </el-button>

          <el-button
            type="info"
            size="default"
            :icon="IconContentSaveMinus"
            @click="cancelCosts"
          >
            Cancelar
          </el-button>
        </template>
      </template>
    </main-buttons-bar>
    <div v-if="!editingCosts">
      <material-filters />
    </div>
    <div
      v-else
      class="cost-panel"
    >
      <custom-input-number
        v-model="adjustmentPercent"
        :spanColumn="3"
        label="Porcentaje de ajuste"
        :settings="{
          placeholder: 'Porcentaje',
          min: 0,
          precision: 0,
          step: 0.01,
          controls: false,
        }"
      />
      <el-button
        size="default"
        type="primary"
        @click="applyAdjustment"
      >
        Aplicar cambios
      </el-button>
    </div>
    <custom-table
      :requestApi="findAllMaterialesHTTP"
      :requestArgs="{ v: materialStore.filtersVersion, 'tipos.ne': [30] }"
      :pathDetail="'/page/material/detail'"
      :pathEdit="'/page/material/edit'"
      :paramId="'Codigo'"
      :data="materialList"
      :columns="columns"
      :showDetailButton="false"
      fixedActions="right"
    >
      <template #custom-actions="{ row }">
        <el-tooltip
          v-if="!canEdit"
          content="El usuario no posee permisos de edición"
          placement="top"
        >
          <el-button
            :icon="IconContentCopy"
            disabled
          />
        </el-tooltip>
        <el-button
          v-else
          :icon="IconContentCopy"
          @click.prevent="duplicateRow(row)"
        />
      </template>

      <template #tipo="{ row }">
        {{ row.MaterialTipo?.Descripcion }}
      </template>

      <template #costo="{ row }">
        <template v-if="editingCosts">
          <custom-input-number
            v-model="row._editCosto"
            :settings="{
              placeholder: 'Costo',
              min: 0,
              precision: 2,
              step: 0.01,
              controls: false,
            }"
          />
        </template>
        <template v-else>
          {{ row.Costo }}
        </template>
      </template>
    </custom-table>
  </permission-gate>
</template>

<script setup>
import {
  ref,
  onBeforeMount,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useMaterialStore } from '@/stores/materiales_store.js';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import CustomTable from '@/shared/components/custom_table.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import IconAdd from '~icons/mdi/add';
import IconCashOpen from '~icons/mdi/cash-lock-open';
import IconContentSave from '~icons/mdi/content-save-check';
import IconContentSaveMinus from '~icons/mdi/content-save-minus-outline';
import MaterialFilters from './material_filters.vue';
import { useFormaStore } from '@/stores/forma/forma_store';
import { useMaterialTipoStore } from '@/stores/material-tipo.js';
import IconContentCopy from '~icons/mdi/content-copy';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import {
  ElMessage,
  ElMessageBox,
} from 'element-plus';
import { useLoading } from '@/shared/composables/loading.js';
import { useCategoriaIVAStore } from '@/stores/categoria_iva_store.js';
import PermissionGate from '@/shared/components/permission_gate.vue';
const formaStore = useFormaStore();
const router = useRouter();
const categoriaIVAStore = useCategoriaIVAStore();

const materialTipoStore = useMaterialTipoStore();
const materialStore = useMaterialStore();
const {
  firstMaterial,
  findAllMaterialesHTTP,
  resetFilter,
  findMaterialByCodigoHTTP,
  updateMaterialCostosHTTP,
} = materialStore;
const { firstMaterialTipo } = materialTipoStore;
const { materialList, material } = storeToRefs(materialStore);
const { firstForma } = formaStore;
const { firstCategoriasIVA } = categoriaIVAStore;
const editingCosts = ref(false);
const adjustmentPercent = ref(0);
const originalCosts = ref([]);

// local state
const items = [
  { label: 'Inicio', to: '/dashboard', replace: false },
  { label: 'Listado', to: null, replace: false },
];

const columns = [
  { field: 'Codigo', label: 'Código', width: 90, sortable: true, isVisible: true },
  {
    field: 'CodigoAdicional',
    label: 'Codigo adicional',
    width: 140,
    sortable: true,
    isVisible: true,
  },
  { field: 'MateriaPrimaFamilia', label: 'Familia', width: 240, sortable: true, isVisible: true },
  {
    field: 'DescripcionInterna',
    label: 'Descripcion interna',
    width: 400,
    sortable: true,
    isVisible: true,
  },
  {
    field: 'MonedaCodigoInternacional',
    label: 'Moneda',
    width: 150,
    sortable: true,
    isVisible: true,
  },
  { field: 'Costo', label: 'Costo', width: 80, sortable: true, isVisible: true, slot: 'costo' },
  { field: 'UnidadMedida', label: 'Unidad medida', width: 130, sortable: true, isVisible: true },
];

function enterCostMode() {
  originalCosts.value = materialList.value.map((m) => ({ Codigo: m.Codigo, Costo: m.Costo }));
  materialList.value.forEach((m) => {
    m._editCosto = m.Costo;
  });
  editingCosts.value = true;
}

function applyAdjustment() {
  const pct = adjustmentPercent.value / 100;
  materialList.value.forEach((m) => {
    m._editCosto = +(m._editCosto * (1 + pct)).toFixed(2);
  });
}

function cancelCosts() {
  // restaura el costo original
  materialList.value.forEach((m) => {
    const orig = originalCosts.value.find((o) => o.Codigo === m.Codigo);
    m._editCosto = orig?.Costo ?? m.Costo;
  });
  editingCosts.value = false;
  adjustmentPercent.value = 0;
}

async function saveCosts() {
  const toUpdate = materialList.value
    .filter((m) => m._editCosto !== m.Costo)
    .map((m) => ({ Codigo: m.Codigo, Costo: m._editCosto }));

  const count = toUpdate.length;
  try {
    await ElMessageBox.confirm(
      `Se actualizarán los costos de ${count} Items. ¿Confirma la actualización?`,
      'Confirmar actualización',
      { type: 'warning' },
    );
    useLoading().openLoading();
    await updateMaterialCostosHTTP(toUpdate);
    useLoading().closeLoading();
    ElMessage.success('Costos guardados correctamente');
    editingCosts.value = false;
    adjustmentPercent.value = 0;
  } catch {
    adjustmentPercent.value = 0;
    useLoading().closeLoading();
  }
}

async function duplicateRow(row) {
  await findMaterialByCodigoHTTP(row.Codigo);
  const materialCopy = JSON.parse(JSON.stringify(materialStore.material));
  materialCopy.Activo = materialCopy.Activo !== false;
  materialCopy.Codigo = null;
  Object.assign(material.value, materialCopy);
  router.push('/page/material/edit');
}

function newMaterial() {
  materialStore.resetMaterial();
  router.push('/page/material/edit');
}

onBeforeMount(() => {
  resetFilter();
  firstCategoriasIVA();
  firstMaterialTipo(0);
  firstForma();
  firstMaterial({ 'tipos.ne': [30] });
});
</script>

<style scoped>
.cost-panel {
  display: flex;
  align-items: flex-end;
  background-color: white;
  border: 3px solid lightgray;
  padding: 20px;
}

.cost-panel .el-button {
  margin-left: 8px; /* separa solo 8px del input */
}
</style>
